import { useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Briefcase, Circle, HeartPulse, Users, Wallet } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { messageDir } from '../../../lib/message-direction';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { queryKeys } from '../../../lib/query-client';
import type { OpenAsksRead } from '../../../lib/open-asks';
import { chaseItemPath, closeChaseItem, type ChaseCloseCache, type ClientSignalPost } from '../../../lib/chase-item';
import {
  CHASE_ACTION_DISMISS_KEY,
  CHASE_ACTION_DONE_KEY,
  CHASE_CATEGORY_GLYPH,
  CHASE_DUE_KEY,
  CHASE_ERROR_KEY,
  CHASE_GROUP_TITLE_KEY,
  CHASE_SOURCE_KEY,
  chaseDueLabel,
  chaseStakesKey,
  type ChaseAsk,
} from '../../../lib/chase-asks';
import { Badge, Button, Card, Toast } from '../ui';
import { CategoryList, useCategoryTitleClass } from '../category-view/CategoryView';
import styles from './tasks.module.css';

// The Action needed tab's "While you were away" group (docs/2026-10-23-chaser-design.md §10, plan 3.6): the
// chaser's OWED items — what the owner was told to do while away — after every other row, by stakes. The same
// rows, words and actions as iOS / Android. A row opens the item's page (`/chase/<itemId>`); "I did it" / "Stop
// watching" close the item through `/api/client-signal` (CHASE_ITEM_DONE / CHASE_ITEM_DISMISSED), optimistically,
// through the one close the item page runs too (`closeChaseItem`): the row leaves at once, the item is remembered
// as closed for this page session (so a racing refresh cannot bring it back), and a refused close puts back only
// that row with the save error.

const GLYPHS = { wallet: Wallet, heart: HeartPulse, users: Users, briefcase: Briefcase, circle: Circle } as const;

export function ChaseAskGroup({ asks }: { asks: ChaseAsk[] }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  if (asks.length === 0) return null;
  return (
    <section className={styles.chaseGroup} data-tasks-chase-group>
      <div className={styles.laterHeader}>{t(CHASE_GROUP_TITLE_KEY)}</div>
      <CategoryList>
        {asks.map((ask) => (
          <ChaseAskRow key={ask.id} ask={ask} onFailed={() => setFailed(true)} />
        ))}
      </CategoryList>
      <Toast open={failed} message={t(CHASE_ERROR_KEY)} onClose={() => setFailed(false)} />
    </section>
  );
}

function ChaseAskRow({ ask, onFailed }: { ask: ChaseAsk; onFailed: () => void }) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const titleCap = useCategoryTitleClass();
  const { chase } = ask;
  const Glyph = GLYPHS[CHASE_CATEGORY_GLYPH[chase.category]];
  const stakesKey = chaseStakesKey(chase.stakes);
  // A short date in the APP's language (month + day, the year only when it is not this year).
  const due = chaseDueLabel(chase.dueAt, Date.now(), i18n.language);
  const source = chase.source ? `${t(CHASE_SOURCE_KEY[chase.source])}${chase.verifiedSender ? ` · ${chase.verifiedSender}` : ''}` : '';
  const bi = { chase_category: chase.category, chase_stakes: chase.stakes };

  const open = () => {
    logPageClickBi({ ...TASKS_PAGE, button_name: 'open_chase_item' }, { additionalFields: bi });
    void router.push(chaseItemPath(chase.itemId));
  };

  const close = async (action: 'done' | 'dismiss') => {
    if (busy) return;
    setBusy(true);
    logPageClickBi({ ...TASKS_PAGE, button_name: action === 'done' ? 'chase_item_done' : 'chase_item_dismissed' }, { additionalFields: bi });
    const ok = await closeChaseItem(ask, action, chaseCloseCache(queryClient), postClientSignal);
    if (!ok) {
      setBusy(false);
      onFailed();
    }
  };

  // The card is the hit target (open the item's page) AND keeps its two real Buttons, so it is a div with an
  // onClick that ignores clicks landing on a control — the DS's nested-control exception (no role/tabIndex: the
  // title is not a second control).
  const onCardClick = (e: { target: EventTarget }) => {
    if ((e.target as HTMLElement).closest('a,button')) return;
    open();
  };

  return (
    <div className={styles.cardRow} data-tasks-chase={chase.itemId}>
      <Card className={`${styles.card} ${styles.chaseCard}`} interactive onClick={onCardClick}>
        <div className={styles.chaseRow}>
          <span className={styles.chaseHead}>
            <Glyph size={16} aria-hidden className={styles.chaseGlyph} />
            {ask.title ? (
              <span className={titleCap ? `${styles.askTitle} ${titleCap}` : styles.askTitle} dir={messageDir(ask.title)}>
                {ask.title}
              </span>
            ) : null}
            {stakesKey ? <Badge variant={chase.stakes === 'critical' ? 'waiting-on-you' : 'waiting-on-a-reply'} label={t(stakesKey)} /> : null}
          </span>
          {due ? <span className={styles.askBody}>{t(CHASE_DUE_KEY, { date: due })}</span> : null}
          {source ? (
            <span className={styles.askBody} dir="auto">
              {source}
            </span>
          ) : null}
          <span className={styles.chaseActions}>
            <Button size="small" variant="contained" onClick={() => void close('done')} disabled={busy}>
              {t(CHASE_ACTION_DONE_KEY)}
            </Button>
            <Button size="small" variant="outlined" onClick={() => void close('dismiss')} disabled={busy}>
              {t(CHASE_ACTION_DISMISS_KEY)}
            </Button>
          </span>
        </div>
      </Card>
    </div>
  );
}

/** The React Query half of a chase close — shared with the item page (`pages/chase/[itemId].tsx`). */
export function chaseCloseCache(queryClient: QueryClient): ChaseCloseCache {
  return {
    updateOpenAsks: (update) => queryClient.setQueryData<OpenAsksRead | null | undefined>(queryKeys.openAsks, update),
    invalidateTasks: () => void queryClient.invalidateQueries({ queryKey: queryKeys.tasksAllScopes }),
  };
}

/** POST a `/api/client-signal` body — the chase close and the item page's opened signal. */
export const postClientSignal: ClientSignalPost = (body) =>
  apiFetch(`${API_BASE}/api/client-signal`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });
